import {
  FormInputGroupLayout,
  FormValues,
  getValue,
  InputForm,
  InputFormComponent,
  InputFormGroup,
  Select,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import {
  DefaultButton,
  FocusZone,
  FocusZoneTabbableElements,
  IButtonStyles,
  mergeStyleSets,
  PrimaryButton,
  Stack,
} from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { Settings, SettingsContext, SettingType } from "../../console/SettingsContext";
import { DetailsPanelId, InlineFormId, PageId } from "../../constants/pluginConstants";
import { footerHeight } from "../../constants/uiConstants";
import { getSupportedLocales } from "../../helpers/supportedLocales";
import { useAnalytics } from "../../hooks/useAnalytics";

export const enum Fields {
  Language = "language",
}

export enum ButtonTestIds {
  ApplySettings = "apply-language-settings",
  DiscardSettings = "discard-language-settings",
}
export enum FieldTestIds {
  LanguageDropdownTestId = "select-language-dropdown"
}

const classNames = mergeStyleSets({
  actionFooter: {
    position: "fixed",
    padding: "10px 10px 0px 10px",
    bottom: `${footerHeight}px`,
    width: "100%",
    height: "44px",
  },
  buttons: {
    justifyContent: "flex-start",
    marginTop: 3,
  },
  languageSelectContainer: { width: "50%", minWidth: "107px", maxWidth: "404px" },
});

const btnStyles: IButtonStyles = { root: { height: 24 } };

export const SettingLanguage = (): JSX.Element => {
  const { trackFormSave } = useAnalytics();

  const formComponentRef = React.useRef<InputFormComponent>();
  const setFormComponentRef = (formComponent: InputFormComponent): void => {
    formComponentRef.current = formComponent;
  };

  const { locale, setSettingValue } = React.useContext<Settings>(SettingsContext);

  const [languageStorage, setLanguageStorage] = React.useState<string | undefined>(locale);
  const [language, setLanguage] = React.useState<string>(languageStorage || "en");
  const [languageKey, setLanguageKey] = React.useState<string>(uniqueGUID());

  const [isButtonDisable, setButtonDisable] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (languageStorage && languageStorage === language) {
      setButtonDisable(true);
    } else {
      setButtonDisable(false);
    }
  }, [language, languageStorage]);

  const onClick = (): void => {
    if (!formComponentRef.current?.isValidationEnabled()) {
      formComponentRef.current?.enableValidation();
    }
    formComponentRef.current?.submit();
  };

  const onSubmit = (formValues: FormValues): void => {
    const languageVal = getValue(formValues, Fields.Language, InputFormGroup)?.[0].id;
    setSettingValue(SettingType.LOCALE, languageVal);
    setLanguageStorage(languageVal);

    window.location.reload();
  };

  const onCancel = (): void => {
    setLanguage(languageStorage || "");
    setLanguageKey(uniqueGUID());
  };

  const optionItems = getSupportedLocales().map(item => {
    const { languageTag, endonym } = item;
    return ({ id: languageTag, text: endonym });
  });

  return (
    <InputForm
      layout={FormInputGroupLayout.WIDE}
      componentRef={setFormComponentRef}
      onSubmit={(formValues: FormValues) => {
        onSubmit(formValues);
      }}
    >
      <Stack className={classNames.languageSelectContainer}>
        <Select
          testId={FieldTestIds.LanguageDropdownTestId}
          required
          key={languageKey}
          fieldName={Fields.Language}
          defaultValue={[language]}
          label={Messages.labels.language()}
          onChange={(languageId: string) => {
            setLanguage(languageId);
          }}
          options={optionItems}
          hideSearchBox
        />
      </Stack>
      <footer className={classNames.actionFooter}>
        <FocusZone handleTabKey={FocusZoneTabbableElements.all} isCircularNavigation>
          <Stack className={classNames.buttons} tokens={{ childrenGap: 8 }} horizontal>
            <PrimaryButton
              disabled={isButtonDisable}
              onClick={() => {
                trackFormSave(InlineFormId.SETTING_LANGUAGE, PageId.SETTINGS, DetailsPanelId.LANGUAGE);
                onClick();
              }}
              styles={btnStyles}
              data-test-id={ButtonTestIds.ApplySettings}
            >
              {Messages.actions.apply()}
            </PrimaryButton>
            <DefaultButton
              styles={btnStyles}
              disabled={isButtonDisable}
              onClick={() => {
                onCancel();
              }}
              data-test-id={ButtonTestIds.DiscardSettings}
            >
              {Messages.actions.discardChanges()}
            </DefaultButton>
          </Stack>
        </FocusZone>
      </footer>
    </InputForm>
  );
};
